import "./contractWizard.css";
import { freshModal } from "../../components/freshModal";
import { Modal, message } from "antd";
import React, { useState } from "react";
import {
  Briefcase,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import axiosInstance from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";
import CreateContractCandidate from "./CreateContractCandidate";
import CreateContractMotif from "./CreateContractMotif";
import CreateContractDate from "./CreateContractDate";
import CreateContractLieu from "./CreateContractLieu";
import CreateContractPoste from "./CreateContractPoste";
import CreateContractTask from "./CreateContractTask";
import CreateContractConsigne from "./CreateContractConsigne";
import CreateContractPenibilite from "./CreateContractPenibilite";
import CreateContractContact from "./CreateContractContact";
import CreateContractReference from "./CreateContractReference";
import CreateContractPeriodeEssai from "./CreateContractPeriodeEssai";

const CreateContractFormSession = ({
  client,
  initialContract = {},
  fixedCandidate = false,
  qualifications,
  chantiers,
  isCreateContract,
  setIsCreateContract,
}) => {
  const [current, setCurrent] = useState(0);
  const [contract, setContract] = useState({
    clientPld: client.pld,
    client: client._id,
    contractType: "",
    consigne: "0 : Aucune",
    TabPrimes: [],
    isEditTauxHoraire: false,
    ...initialContract,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const requiresMotif = contract.contractType === "interim";
  const finiteTerm = ["interim", "cdd", "vacation"].includes(
    contract.contractType,
  );
  const formatDate = (value) => {
    const d = new Date(value);
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  };
  const formatHour = (value) => {
    const d = new Date(value);
    return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const isValidDate = (value) =>
    value && !Number.isNaN(new Date(value).getTime());
  const validate = (step) => {
    if (
      step === 0 &&
      !["interim", "cdd", "cdi", "vacation"].includes(contract.contractType)
    )
      return "Choisissez le type de contrat.";
    if (step === 1 && (!contract.contact || !contract.candidate))
      return "Sélectionnez le contact client et le candidat.";
    if (step === 2) {
      if (requiresMotif && !contract.motif)
        return "Sélectionnez le motif de recours.";
      if (
        !isValidDate(contract.startDate) ||
        (finiteTerm && !isValidDate(contract.endDate))
      )
        return "Renseignez les dates du contrat.";
      if (
        contract.endDate &&
        new Date(contract.endDate) < new Date(contract.startDate)
      )
        return "La fin doit être postérieure au début.";
      if (
        !isValidDate(contract.startHour) ||
        !isValidDate(contract.endHour)
      )
        return "Renseignez les horaires du contrat.";
    }
    if (
      step === 3 &&
      (!contract.address ||
        !contract.postCode ||
        !contract.town ||
        (!contract.clientQualification?.IdQualification &&
          !contract.clientQualification?.TxtQualification))
    )
      return "Renseignez le poste et l’adresse de la mission.";
    return null;
  };
  const next = () => {
    const error = validate(current);
    if (error) message.error(error);
    else setCurrent(current + 1);
  };
  const prev = () => setCurrent(Math.max(0, current - 1));
  const validateContract = async () => {
    if (isCreating) return;
    const error = [0, 1, 2, 3, 4].map(validate).find(Boolean);
    if (error) {
      message.error(error);
      return;
    }
    setSubmissionError("");
    setIsCreating(true);
    const payload = {
      ...contract,
      candidate: contract.candidate?._id || contract.candidate,
      client: contract.client?._id || contract.client || client._id,
      startDateString: formatDate(contract.startDate),
      startHourString: formatHour(contract.startHour),
      endHourString: formatHour(contract.endHour),
    };
    if (finiteTerm) payload.endDateString = formatDate(contract.endDate);
    else {
      delete payload.endDate;
      delete payload.endDateString;
    }
    if (!requiresMotif) {
      delete payload.motif;
      delete payload.otherMotif;
      delete payload.justificatif;
    }
    try {
      await axiosInstance.post("demande-contrat", payload);
      message.success("Demande de contrat envoyée à l’agence.");
      setIsCreateContract(false);
    } catch (error) {
      const detail = apiErrorMessage(
        error,
        "La demande n’a pas pu être envoyée. Vos informations sont conservées, vérifiez-les puis réessayez",
      );
      setSubmissionError(detail);
      if (detail) message.error(detail);
    } finally {
      setIsCreating(false);
    }
  };

  const common = { contract, setContract };
  const panel = (content) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
      {content}
    </div>
  );
  const steps = [
    {
      title: "Type de contrat",
      content: (
        <div>
          <p className="mb-5 text-sm text-slate-500">
            Quel contrat souhaitez-vous préparer pour ce client ?
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              {
                id: "interim",
                label: "Intérim",
                text: "Mission temporaire avec motif, dates et horaires.",
              },
              {
                id: "cdd",
                label: "CDD",
                text: "Contrat à durée déterminée avec dates, horaires et poste.",
              },
              {
                id: "cdi",
                label: "CDI",
                text: "Contrat sans date de fin, avec date de début et horaires.",
              },
              {
                id: "vacation",
                label: "Vacation",
                text: "Vacation avec dates, horaires et informations du poste.",
              },
            ].map((type) => (
              <button
                key={type.id}
                aria-pressed={contract.contractType === type.id}
                onClick={() =>
                  setContract((current) => ({
                    ...current,
                    client: client._id,
                    clientPld: client.pld,
                    contractType: type.id,
                  }))
                }
                className={`rounded-xl border p-4 text-left transition ${contract.contractType === type.id ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" : "border-slate-200 bg-white hover:border-blue-300"}`}
              >
                <span className="flex items-center justify-between font-semibold text-slate-900">
                  {type.label}
                  {contract.contractType === type.id && (
                    <CheckCircle2 size={18} className="text-blue-600" />
                  )}
                </span>
                <p className="mt-2 text-sm leading-5 text-slate-500">
                  {type.text}
                </p>
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Contact et candidat",
      content: (
        <div className="space-y-3">
          {panel(<CreateContractContact client={client} {...common} />)}
          {panel(
            fixedCandidate ? (
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Candidat sélectionné
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {[contract.candidate?.firstname, contract.candidate?.lastname]
                    .filter(Boolean)
                    .join(" ")}
                </p>
              </div>
            ) : (
              <CreateContractCandidate {...common} />
            ),
          )}
        </div>
      ),
    },
    {
      title: requiresMotif ? "Motif et période" : "Dates et horaires",
      content: (
        <div className="space-y-3">
          {requiresMotif &&
            panel(
<CreateContractMotif {...common} />,
            )}
          {panel(<CreateContractDate {...common} finiteTerm={finiteTerm} />)}
        </div>
      ),
    },
    {
      title: "Poste et lieu",
      content: (
        <div className="space-y-3">
          {panel(
            <CreateContractLieu
              client={client}
              chantiers={chantiers || []}
              {...common}
            />,
          )}
          {panel(
            <CreateContractPoste
              qualifications={qualifications || []}
              {...common}
            />,
          )}
        </div>
      ),
    },
    {
      title: "Détails et validation",
      content: (
        <div className="space-y-3">
          {panel(<CreateContractTask {...common} />)}
          {panel(<CreateContractConsigne {...common} />)}
          {panel(<CreateContractReference {...common} />)}
          {panel(<CreateContractPeriodeEssai {...common} />)}
          {contract.contractType === "interim" &&
            panel(<CreateContractPenibilite {...common} />)}
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-5 text-sm text-blue-900">
            <p className="font-semibold">
              Demande à transmettre à l’agence
            </p>
            <p className="mt-2">
              Type : {contract.contractType?.toUpperCase()} · Client :{" "}
              {client.companyName || client.denomination}
            </p>
            <p className="mt-1">
              Début :{" "}
              {contract.startDate
                ? new Date(contract.startDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
                : "—"}
              {finiteTerm &&
                ` · Fin : ${contract.endDate ? new Date(contract.endDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}`}
            </p>
          </div>
        </div>
      ),
    },
  ];
  return (
    <Modal
      className="candidate-contract-wizard"
      open={isCreateContract}
      centered
      width={1000}
      footer={null}
      onCancel={() => {
        if (!isCreating) setIsCreateContract(false);
      }}
      keyboard={!isCreating}
      maskClosable={!isCreating}
      closable={!isCreating}
      title={
        <div className="flex items-center gap-3 pr-6">
          <div className="rounded-xl bg-blue-50 p-3 text-blue-700">
            <Briefcase size={22} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Nouveau contrat
            </h2>
            <p className="mt-1 text-sm font-normal text-slate-500">
              {client.companyName || client.denomination}
              {contract.contractType &&
                ` · ${contract.contractType.toUpperCase()}`}
            </p>
          </div>
        </div>
      }
    >
      <div className="mt-5">
        <ol className="mb-4 grid grid-cols-5 gap-2">
          {steps.map((step, index) => (
            <li
              key={step.title}
              aria-current={current === index ? "step" : undefined}
              className={`flex items-center gap-2 rounded-lg border px-2 py-2 text-xs ${index === current ? "border-blue-200 bg-blue-50 font-semibold text-blue-800" : "border-slate-200 bg-white text-slate-500"}`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${index === current ? "bg-blue-700 text-white" : index < current ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}
              >
                {index < current ? <CheckCircle2 size={14} /> : index + 1}
              </span>
              <span className="hidden sm:inline">{step.title}</span>
            </li>
          ))}
        </ol>
        <div className="max-h-[55vh] overflow-y-auto rounded-xl bg-slate-50 p-4">
          <h3 className="mb-3 text-base font-semibold text-slate-900">
            {steps[current].title}
          </h3>
          {steps[current].content}
        </div>
        {submissionError && (
          <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {submissionError}
          </p>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <button
            disabled={isCreating}
            onClick={() => (current ? prev() : setIsCreateContract(false))}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-600 disabled:opacity-50"
          >
            {current > 0 && <ArrowLeft size={16} />}
            {current ? "Précédent" : "Annuler"}
          </button>
          <p className="text-xs text-slate-400">
            Étape {current + 1} sur {steps.length}
          </p>
          <button
            disabled={isCreating}
            onClick={current === steps.length - 1 ? validateContract : next}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
          >
            {isCreating ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Envoi…
              </>
            ) : current === steps.length - 1 ? (
              "Envoyer la demande"
            ) : (
              <>
                Suivant
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default freshModal(CreateContractFormSession, "isCreateContract");
